import { useSnapshot } from 'valtio';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import CharacterIcon from '@cartwhl/ui/icons/CharacterIcon';
import ToggleButton from '@cartwhl/ui/input/ToggleButton';
import OverlayButtonStack from '@cartwhl/ui/player/OverlayButtonStack';

import { CANVAS_ON_OPEN_WIZARD_TARGET_IDS } from 'wizards/canvasWizards';

import {
  authStore,
  characterPickerStore,
  noticesStore,
  projectStore,
} from 'stores';
import { PanelKey } from 'stores/uiStore';

export default function PanelButtons({
  openPanel,
  setOpenPanel,
}: {
  openPanel: string;
  setOpenPanel: (value: PanelKey) => void;
}) {
  const { characters } = useSnapshot(characterPickerStore.state);
  const { authStatus } = useSnapshot(authStore.state);
  const { currentProject } = useSnapshot(projectStore.state);
  const activeCharacterID = Object.values(
    currentProject?.data?.characters ?? {},
  )[0]?.characterID;
  const activeCharacter = characters?.find(
    (c) => c.jobID === activeCharacterID,
  );
  const isLg = useMediaQuery('(min-width: 1024px)');

  return (
    <div
      className={`dark:bg-gray-90/40 bg-gray-15/40 pointer-events-auto flex flex-col gap-2 rounded-2xl p-1.5 backdrop-blur-md lg:p-3`}
    >
      <div className="flex flex-col items-end gap-2">
        <OverlayButtonStack.PreviewButton
          data-wizard-target={CANVAS_ON_OPEN_WIZARD_TARGET_IDS.character}
          tooltipText="Character"
          text="Character"
          icon={<CharacterIcon />}
          imageSrc={activeCharacter?.heroThumbnailPath}
          imageAlt={
            activeCharacter?.characterName
              ? `Current character is ${activeCharacter.characterName}`
              : 'Character'
          }
          fallback={<CharacterIcon />}
          active={openPanel === 'character'}
          onClick={() =>
            setOpenPanel(openPanel === 'character' ? '' : 'character')
          }
        />
        <Divider />
        <div className="pointer-events-auto flex w-[30px] flex-col gap-2 lg:w-[119px]">
          <Tooltip
            color="default"
            text="Stage Settings"
            side="left"
            disabled={isLg}
          >
            <ToggleButton
              text="Stage Settings"
              active={openPanel === 'unlimited'}
              icon={<AdvancedIcon />}
              onClick={() =>
                setOpenPanel(openPanel === 'advanced' ? '' : 'advanced')
              }
            />
          </Tooltip>

          <Divider />

          {/* <Tooltip color="default" text="Label" side="left" disabled={isLg}>
            <ToggleButton
            text="Label"
            active={openPanel === 'label'}
            icon={<LabelIcon />}
            onClick={() => setOpenPanel(openPanel === 'label' ? '' : 'label')}
          /> 
          </Tooltip>*/}

          <Tooltip color="default" text="History" side="left" disabled={isLg}>
            <ToggleButton
              text="History"
              active={openPanel === 'history'}
              icon={<HistoryIcon />}
              onClick={() => {
                if (authStatus === 'anonymous') {
                  noticesStore.error('You must be logged in to view history');
                  return;
                }
                setOpenPanel(openPanel === 'history' ? '' : 'history');
              }}
            />
          </Tooltip>

          <Tooltip color="default" text="Learn" side="left" disabled={isLg}>
            <ToggleButton
              text="Learn"
              active={openPanel === 'start'}
              icon={<LearnIcon />}
              onClick={() => setOpenPanel(openPanel === 'start' ? '' : 'start')}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

// #region Helper components

const Divider = ({ className }: { className?: string }) => {
  return (
    <hr
      className={`border-gray-30 dark:border-gray-75 hidden h-px w-full lg:block ${className}`}
    />
  );
};

// #endregion Helper components

const AdvancedIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="transparent"
      stroke="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M10.0152 13.1919L15.9999 13.1919" strokeLinecap="round" />
      <path d="M4.03058 13.1919H5.32727" strokeLinecap="round" />
      <path d="M4.03058 6.80811H9.21734" strokeLinecap="round" />
      <path d="M14.0051 6.8082L16 6.8082" strokeLinecap="round" />
      <circle cx="7.92062" cy="13.2417" r="1.94504" />
      <circle cx="11.8107" cy="6.75827" r="1.94504" />
    </svg>
  );
};

const HistoryIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M10.3872 6.73535C10.5396 6.73535 10.6686 6.79036 10.7744 6.90039C10.8844 7.00618 10.9395 7.13525 10.9395 7.2876V11.5596L10.3491 9.75049L12.4121 12.5308C12.5179 12.6662 12.5645 12.8101 12.5518 12.9624C12.5391 13.1147 12.4692 13.2354 12.3423 13.3242C12.2026 13.4215 12.0566 13.4575 11.9043 13.4321C11.7562 13.4067 11.625 13.32 11.5107 13.1719L9.99365 11.1724C9.93864 11.1004 9.89844 11.0306 9.87305 10.9629C9.84766 10.891 9.83496 10.8148 9.83496 10.7344V7.2876C9.83496 7.13525 9.88786 7.00618 9.99365 6.90039C10.1037 6.79036 10.2349 6.73535 10.3872 6.73535ZM10.6284 16.9741C9.94287 16.9741 9.27848 16.8662 8.63525 16.6504C7.99626 16.4388 7.40592 16.1383 6.86426 15.749C6.32682 15.3639 5.86133 14.9175 5.46777 14.4097C5.32812 14.2404 5.26888 14.0648 5.29004 13.8828C5.3112 13.7008 5.39372 13.557 5.5376 13.4512C5.69417 13.3411 5.8571 13.3052 6.02637 13.3433C6.19564 13.3813 6.34587 13.4744 6.47705 13.6226C6.79443 14.0415 7.16683 14.4054 7.59424 14.7144C8.02588 15.019 8.49561 15.2539 9.00342 15.4189C9.51546 15.5882 10.0571 15.675 10.6284 15.6792C11.3563 15.6834 12.0376 15.548 12.6724 15.2729C13.3114 15.0021 13.87 14.6276 14.3481 14.1494C14.8306 13.667 15.2072 13.1084 15.478 12.4736C15.7531 11.8346 15.8906 11.1491 15.8906 10.417C15.8906 9.68913 15.7531 9.00781 15.478 8.37305C15.2072 7.73405 14.8306 7.17546 14.3481 6.69727C13.87 6.21484 13.3114 5.83822 12.6724 5.56738C12.0376 5.29655 11.3563 5.16113 10.6284 5.16113C9.89632 5.16113 9.21289 5.29655 8.57812 5.56738C7.94759 5.83398 7.39111 6.20638 6.90869 6.68457C6.42627 7.16276 6.04753 7.71924 5.77246 8.354C5.50163 8.98454 5.3641 9.66374 5.35986 10.3916H4.06494C4.06917 9.49447 4.24268 8.65234 4.58545 7.86523C4.92822 7.07812 5.40218 6.38411 6.00732 5.7832C6.61247 5.18229 7.30859 4.71257 8.0957 4.37402C8.88704 4.03125 9.73128 3.85986 10.6284 3.85986C11.5256 3.85986 12.3698 4.03125 13.1611 4.37402C13.9525 4.7168 14.6486 5.19076 15.2495 5.7959C15.8547 6.39681 16.3286 7.09294 16.6714 7.88428C17.0142 8.67562 17.1855 9.51986 17.1855 10.417C17.1855 11.3141 17.0142 12.1584 16.6714 12.9497C16.3286 13.741 15.8547 14.4393 15.2495 15.0444C14.6486 15.6496 13.9525 16.1214 13.1611 16.46C12.3698 16.8027 11.5256 16.9741 10.6284 16.9741ZM3.30322 9.12207H6.43896C6.59977 9.12207 6.72038 9.16016 6.80078 9.23633C6.88542 9.30827 6.92562 9.40137 6.92139 9.51562C6.91715 9.62565 6.87272 9.74414 6.78809 9.87109L5.24561 12.0103C5.13981 12.1541 5.01497 12.2282 4.87109 12.2324C4.72721 12.2324 4.60026 12.1584 4.49023 12.0103L2.94775 9.87109C2.86312 9.74837 2.81868 9.62988 2.81445 9.51562C2.81022 9.40137 2.84831 9.30827 2.92871 9.23633C3.01335 9.16016 3.13818 9.12207 3.30322 9.12207Z" />
    </svg>
  );
};

const LearnIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M16.5 5H12C11.6119 5 11.2291 5.08283 10.882 5.24194C10.5348 5.40104 10.2329 5.63205 10 5.91667C9.76713 5.63205 9.46517 5.40104 9.11803 5.24194C8.7709 5.08283 8.38811 5 8 5H3.5C3.36739 5 3.24021 5.04829 3.14645 5.13424C3.05268 5.2202 3 5.33678 3 5.45833V13.7083C3 13.8299 3.05268 13.9465 3.14645 14.0324C3.24021 14.1184 3.36739 14.1667 3.5 14.1667H8C8.39782 14.1667 8.77936 14.3115 9.06066 14.5694C9.34196 14.8273 9.5 15.177 9.5 15.5417C9.5 15.6632 9.55268 15.7798 9.64645 15.8658C9.74022 15.9517 9.86739 16 10 16C10.1326 16 10.2598 15.9517 10.3536 15.8658C10.4473 15.7798 10.5 15.6632 10.5 15.5417C10.5 15.177 10.658 14.8273 10.9393 14.5694C11.2206 14.3115 11.6022 14.1667 12 14.1667H16.5C16.6326 14.1667 16.7598 14.1184 16.8536 14.0324C16.9473 13.9465 17 13.8299 17 13.7083V5.45833C17 5.33678 16.9473 5.2202 16.8536 5.13424C16.7598 5.04829 16.6326 5 16.5 5ZM8 13.25H4V5.91667H8C8.39782 5.91667 8.77936 6.06153 9.06066 6.31939C9.34196 6.57726 9.5 6.92699 9.5 7.29167V13.7083C9.06767 13.4101 8.54107 13.2492 8 13.25ZM16 13.25H12C11.4589 13.2492 10.9323 13.4101 10.5 13.7083V7.29167C10.5 6.92699 10.658 6.57726 10.9393 6.31939C11.2206 6.06153 11.6022 5.91667 12 5.91667H16V13.25ZM12 7.29167H14.5C14.6326 7.29167 14.7598 7.33996 14.8536 7.42591C14.9473 7.51186 15 7.62844 15 7.75C15 7.87156 14.9473 7.98814 14.8536 8.07409C14.7598 8.16004 14.6326 8.20833 14.5 8.20833H12C11.8674 8.20833 11.7402 8.16004 11.6464 8.07409C11.5527 7.98814 11.5 7.87156 11.5 7.75C11.5 7.62844 11.5527 7.51186 11.6464 7.42591C11.7402 7.33996 11.8674 7.29167 12 7.29167ZM15 9.58333C15 9.70489 14.9473 9.82147 14.8536 9.90742C14.7598 9.99338 14.6326 10.0417 14.5 10.0417H12C11.8674 10.0417 11.7402 9.99338 11.6464 9.90742C11.5527 9.82147 11.5 9.70489 11.5 9.58333C11.5 9.46178 11.5527 9.3452 11.6464 9.25924C11.7402 9.17329 11.8674 9.125 12 9.125H14.5C14.6326 9.125 14.7598 9.17329 14.8536 9.25924C14.9473 9.3452 15 9.46178 15 9.58333ZM15 11.4167C15 11.5382 14.9473 11.6548 14.8536 11.7408C14.7598 11.8267 14.6326 11.875 14.5 11.875H12C11.8674 11.875 11.7402 11.8267 11.6464 11.7408C11.5527 11.6548 11.5 11.5382 11.5 11.4167C11.5 11.2951 11.5527 11.1785 11.6464 11.0926C11.7402 11.0066 11.8674 10.9583 12 10.9583H14.5C14.6326 10.9583 14.7598 11.0066 14.8536 11.0926C14.9473 11.1785 15 11.2951 15 11.4167Z" />
    </svg>
  );
};

// #endregion Icons
